import { useLanguage } from '@/contexts/LanguageContext';

export default function PartnershipSupport() {
  const { t } = useLanguage();

  const supports = [
    {
      icon: 'ri-presentation-line',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      number: '01',
      title: t('技術簡報與產品教育', 'Technical Presentations & Product Training', '技術プレゼン・製品教育'),
      desc: t(
        '提供完整的 PQernel®、PQMB 與 rvPlastics 技術簡報，協助夥伴快速掌握產品知識與應用場景。',
        'Provide comprehensive PQernel®, PQMB, and rvPlastics technical presentations to help partners quickly master product knowledge and application scenarios.',
        'PQernel®・PQMB・rvPlasticsの包括的な技術プレゼンを提供し、パートナーが製品知識と応用シーンを迅速に習得できるよう支援します。'
      ),
    },
    {
      icon: 'ri-test-tube-line',
      iconColor: 'text-brand-blue',
      iconBg: 'bg-brand-blue-light',
      number: '02',
      title: t('樣品與測試建議', 'Samples & Testing Recommendations', 'サンプル・試験提案'),
      desc: t(
        '提供產品樣品包，並依客戶原料條件給予測試建議，協助夥伴快速完成客戶端驗證。',
        'Provide product sample kits and testing recommendations based on client raw material conditions, helping partners quickly complete client-side verification.',
        '製品サンプルキットを提供し、クライアントの原料条件に基づく試験提案を行い、パートナーがクライアント側の検証を迅速に完了できるよう支援します。'
      ),
    },
    {
      icon: 'ri-file-text-line',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      number: '03',
      title: t('應用案例與行銷素材', 'Application Cases & Marketing Materials', '応用事例・マーケティング素材'),
      desc: t(
        '提供應用案例、技術數據與行銷素材，協助夥伴在客戶端建立專業形象與說服力。',
        'Provide application cases, technical data, and marketing materials to help partners establish professional credibility with clients.',
        '応用事例・技術データ・マーケティング素材を提供し、パートナーがクライアントに対してプロフェッショナルな信頼性を構築できるよう支援します。'
      ),
    },
    {
      icon: 'ri-team-line',
      iconColor: 'text-brand-blue',
      iconBg: 'bg-brand-blue-light',
      number: '04',
      title: t('共同客戶簡報與提案', 'Joint Client Presentations & Proposals', '共同顧客プレゼン・提案'),
      desc: t(
        '聚量技術團隊可陪同夥伴進行客戶簡報與技術提案，提升成交率與客戶信任度。',
        'PolyQ\'s technical team can accompany partners in client presentations and technical proposals, improving conversion rates and client trust.',
        '聚量の技術チームがパートナーの顧客プレゼンと技術提案に同行し、成約率と顧客信頼度を向上させます。'
      ),
    },
    {
      icon: 'ri-flask-line',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      number: '05',
      title: t('專案型技術導入與試產陪跑', 'Project-Based Technical Implementation & Pilot Support', 'プロジェクト型技術導入・試験生産サポート'),
      desc: t(
        '針對重要客戶或示範場域，聚量可提供專案型技術導入服務，全程陪跑試產與物性驗證。',
        'For key clients or demonstration sites, PolyQ can provide project-based technical implementation services with full pilot production and property verification support.',
        '重要クライアントや実証サイトに対して、聚量はプロジェクト型技術導入サービスを提供し、試験生産と物性検証を全面的にサポートします。'
      ),
    },
    {
      icon: 'ri-global-line',
      iconColor: 'text-brand-blue',
      iconBg: 'bg-brand-blue-light',
      number: '06',
      title: t('官網與對外資料合作露出', 'Website & External Material Co-Exposure', 'ウェブサイト・対外資料での共同露出'),
      desc: t(
        '符合條件的合作夥伴可在聚量官網與對外資料中獲得露出，提升品牌可信度與市場能見度。',
        'Qualified partners can receive exposure on PolyQ\'s website and external materials, enhancing brand credibility and market visibility.',
        '条件を満たすパートナーは聚量のウェブサイトと対外資料に掲載され、ブランドの信頼性と市場での認知度を高めることができます。'
      ),
    },
  ];

  return (
    <section className="section-padding bg-[#F5F8FB]">
      <div className="max-w-screen-xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <p className="text-[1rem] font-semibold text-brand-green uppercase tracking-widest mb-3">
            {t('我們提供的支持', 'What We Provide', '私たちが提供するサポート')}
          </p>
          <h2
            className="font-bold text-neutral-dark mb-4"
            style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)', lineHeight: '1.4' }}
          >
            {t('聚量可提供的支持', 'Support PolyQ Can Provide', '聚量が提供できるサポート')}
          </h2>
          <p className="text-neutral-mid max-w-xl mx-auto" style={{ fontSize: '1.1875rem', lineHeight: '1.75' }}>
            {t(
              '從技術知識到市場資源，聚量為合作夥伴提供全方位的支持，讓合作從第一天就能快速啟動。',
              'From technical knowledge to market resources, PolyQ provides comprehensive support for partners, enabling rapid launch from day one.',
              '技術知識から市場リソースまで、聚量はパートナーに包括的なサポートを提供し、初日から迅速なスタートを可能にします。'
            )}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {supports.map((s) => (
            <div
              key={s.number}
              className="group bg-white rounded-card border border-neutral-border p-6 hover:-translate-y-0.5 transition-all duration-200"
              style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${s.iconBg} flex-shrink-0`}>
                  <i className={`${s.icon} ${s.iconColor} text-2xl`}></i>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[0.8125rem] font-bold text-neutral-mid tracking-widest">{s.number}</span>
                    <h3 className="font-semibold text-neutral-dark group-hover:text-brand-green transition-colors" style={{ fontSize: '1.0625rem', lineHeight: '1.5' }}>{s.title}</h3>
                  </div>
                  <p className="text-neutral-mid text-[1rem] leading-relaxed">{s.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
